import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Avatar,
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import ContactMailIcon from '@mui/icons-material/ContactMail';

import { useAuth } from '../../../context/AuthContext';

const logo = require('../../../assets/logo.png');

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { loggedIn } = useAuth();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isVending, setIsVending] = useState(location.pathname.includes('vending'));

  useEffect(() => {
    setIsVending(location.pathname.includes('vending'));
  }, [location.pathname]);

  const navLinks = [
    { label: 'Home', icon: <HomeIcon />, path: isVending ? '/vending' : '/' },
    {
      label: 'About',
      icon: <InfoIcon />,
      path: isVending ? '/vending/about' : '/about',
    },
    { label: 'Contact', icon: <ContactMailIcon />, path: isVending ? '/vending/contact' : '/contact' },
  ];

  const handleDrawerClose = () => setDrawerOpen(false);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleUserMenuClose = () => setAnchorEl(null);

  const handleLoginRedirect = () => {
    handleUserMenuClose();
    navigate('/login');
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: '#111', // Black navbar
          borderBottom: '1px solid #222',
          backdropFilter: 'blur(8px)',
          boxShadow: '0 4px 24px 0 rgba(0,0,0,0.12)',
          borderRadius: 0,
          transition: 'background 0.3s, box-shadow 0.3s',
        }}
      >
        <Toolbar sx={{ minHeight: 70, px: { xs: 1, md: 4 } }}>
          {/* Left: Logo */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.04)',
                },
              }}
              onClick={() => navigate(isVending ? '/vending' : '/')}
            >
              <img src={logo} alt="Global GA logo" style={{ height: 44, width: 90, objectFit: 'contain' }} />
              {!isMobile && (
                <Typography
                  variant="h6"
                  sx={{
                    ml: 1,
                    fontWeight: 700,
                    letterSpacing: 1,
                    fontFamily: '"Playfair Display", serif',
                    fontSize: '1.5rem',
                    color: '#fff', // White text for contrast
                    userSelect: 'none',
                  }}
                >
                  Global GA
                </Typography>
              )}
            </Box>
          </Box>

          {/* Center: Nav Links */}
          {!isMobile && (
            <Box
              sx={{
                display: 'flex',
                flex: 1,
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
                minWidth: 0,
              }}
            >
              {navLinks.map((link) => (
                <Box
                  key={link.label}
                  sx={{
                    position: 'relative',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                    color: '#fff', // White nav links
                    fontWeight: 500,
                    fontSize: '1rem',
                    '&:hover': {
                      background: '#222', // Slightly lighter black on hover
                    },
                    '&:after': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      left: 12,
                      right: 12,
                      bottom: 6,
                      height: 2,
                      borderRadius: 1,
                      background: theme.palette.primary.main,
                      opacity: 0,
                      transform: 'scaleX(0)',
                      transition: 'all 0.25s cubic-bezier(.4,0,.2,1)',
                    },
                    '&:hover:after': {
                      opacity: 1,
                      transform: 'scaleX(1)',
                    },
                  }}
                  onClick={() => handleNavClick(link.path)}
                >
                  {link.label}
                </Box>
              ))}
            </Box>
          )}

          {/* Right: Account Icon */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              justifyContent: 'flex-end',
              minWidth: 0,
            }}
          >
            <IconButton
              size="large"
              edge="end"
              color="inherit"
              aria-label="account"
              aria-controls="navbar-account-menu"
              aria-haspopup="true"
              onClick={handleUserMenuOpen}
              sx={{
                transition: 'background 0.2s',
                color: '#fff', // White icon
                '&:hover': {
                  background: '#222',
                },
              }}
            >
              {loggedIn ? (
                <Avatar sx={{ width: 36, height: 36, bgcolor: theme.palette.primary.main }}>
                  <AccountCircle sx={{ color: '#fff' }} />
                </Avatar>
              ) : (
                <AccountCircle sx={{ fontSize: 36 }} />
              )}
            </IconButton>
            <Menu
              id="navbar-account-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleUserMenuClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  bgcolor: '#222',
                  color: '#fff',
                },
              }}
            >
              {loggedIn ? (
                [
                  <MenuItem key="profile" onClick={handleUserMenuClose} sx={{ color: '#fff' }}>Profile</MenuItem>,
                  <MenuItem key="logout" onClick={handleUserMenuClose} sx={{ color: '#fff' }}>Logout</MenuItem>,
                ]
              ) : (
                <MenuItem onClick={handleLoginRedirect} sx={{ color: '#fff' }}>Login</MenuItem>
              )}
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer for mobile nav */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: {
            bgcolor: '#111', // Black drawer
            color: '#fff',
            width: 260,
            boxShadow: '0 8px 32px 0 rgba(0,0,0,0.16)',
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          },
        }}
      >
        <Box
          sx={{ width: 260, pt: 2 }}
          role="presentation"
          onClick={handleDrawerClose}
          onKeyDown={handleDrawerClose}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2, mb: 2 }}>
            <img src={logo} alt="Global GA logo" style={{ height: 36, width: 70, objectFit: 'contain' }} />
            <Typography
              variant="h6"
              sx={{
                ml: 1,
                fontWeight: 700,
                fontFamily: '"Playfair Display", serif',
                fontSize: '1.2rem',
                color: '#fff',
              }}
            >
              Global GA
            </Typography>
          </Box>
          <Divider sx={{ mb: 1, bgcolor: '#222' }} />
          <List>
            {navLinks.map((link) => (
              <ListItem key={link.label} disablePadding>
                <ListItemButton
                  onClick={() => handleNavClick(link.path)}
                  sx={{
                    color: '#fff',
                    '&:hover': { background: '#222' },
                  }}
                >
                  <ListItemIcon sx={{ color: '#fff', minWidth: 36 }}>{link.icon}</ListItemIcon>
                  <ListItemText primary={link.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </Box>
  );
};

export default Navbar;
